import React, { useState } from "react";
import {
  Box,
  Typography,
  Collapse,
  IconButton,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
} from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Tooltip } from "@mui/material";
import { exportTableCSV, exportTableExcel } from "./exportUtils";
import { useFullscreen, fullscreenSx, FullscreenBackdrop, FullscreenButton } from "./shared";

/**
 * Reusable collapsible analytics table.
 * props:
 *  - title, subtitle
 *  - columns: [{ key, label, align?, render?(row) }]
 *  - rows: array of objects
 *  - loading
 *  - getRowSx?(row) -> sx for conditional row highlight
 *  - totalsRow?: object (pinned bottom row)
 */
const AnalyticsTable = ({
  title,
  subtitle,
  columns,
  rows = [],
  loading = false,
  getRowSx,
  totalsRow,
  fileName,
  headerExtra,
}) => {
  const [open, setOpen] = useState(true);
  const [isFull, setFull] = useFullscreen();

  // Export uses the raw (non-rendered) values; render() is for display only.
  const exportColumns = columns.map((c) => ({ key: c.key, label: c.label }));
  const handleCSV = () =>
    exportTableCSV(fileName || title, exportColumns, rows);
  const handleExcel = () =>
    exportTableExcel(fileName || title, exportColumns, rows, title);

  return (
    <>
    <FullscreenBackdrop open={isFull} onClose={() => setFull(false)} />
    <Box
      sx={{
        backgroundColor: "var(--app-surface)",
        border: "1px solid var(--app-border)",
        borderRadius: "16px",
        // boxShadow: "var(--app-shadow)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        ...fullscreenSx(isFull),
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          px: { xs: 2, md: 2.5 },
          py: 1.5,
          borderBottom: open ? "1px solid var(--app-border)" : "none",
        }}
      >
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}
        >
          <Box
            sx={{
              width: 4,
              height: 18,
              borderRadius: "4px",
              backgroundColor: "#5751E9",
              flexShrink: 0,
            }}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "16px",
                color: "var(--app-text)",
                fontFamily: "Figtree",
                lineHeight: 1.2,
              }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography
                sx={{
                  fontSize: "12.5px",
                  color: "var(--app-text-muted)",
                  fontFamily: "Figtree",
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexShrink: 0 }}>
          {headerExtra}
          <Button
            onClick={handleExcel}
            disabled={!rows.length}
            startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              fontSize: "13px",
              fontFamily: "Figtree",
              color: "#5751E9",
              borderRadius: "8px",
              px: 1.25,
              "&:hover": { backgroundColor: "var(--app-hover)" },
              "&.Mui-disabled": { color: "#B9BCC2" },
            }}
          >
            Excel
          </Button>
          <Button
            onClick={handleCSV}
            disabled={!rows.length}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              fontSize: "13px",
              fontFamily: "Figtree",
              color: "#5751E9",
              borderRadius: "8px",
              px: 1.25,
              "&:hover": { backgroundColor: "var(--app-hover)" },
              "&.Mui-disabled": { color: "#B9BCC2" },
            }}
          >
            CSV
          </Button>
          <FullscreenButton isFull={isFull} onToggle={() => setFull((f) => !f)} />
          <IconButton size="small" onClick={() => setOpen((o) => !o)}>
            <ExpandMoreRoundedIcon
              sx={{
                transition: "transform .2s ease",
                transform: open ? "rotate(180deg)" : "none",
                color: "var(--app-text-muted)",
              }}
            />
          </IconButton>
        </Box>
      </Box>

      <Collapse
        in={open || isFull}
        timeout="auto"
        unmountOnExit
        sx={isFull ? { flex: 1, minHeight: 0, "& .MuiCollapse-wrapperInner": { height: "100%" } } : undefined}
      >
        {loading ? (
          <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1 }}>
            {/* Wave shimmer shaped like the rows that are about to land, so the
                table does not jump when the data arrives. */}
            <Skeleton animation="wave" variant="rectangular" height={32} sx={{ borderRadius: 1 }} />
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton key={i} animation="wave" variant="rectangular" height={24} sx={{ borderRadius: 1, opacity: 1 - i * 0.13 }} />
            ))}
          </Box>
        ) : rows.length === 0 ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography sx={{ color: "var(--app-text-muted)", fontFamily: "Figtree" }}>
              No data for the selected filters.
            </Typography>
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: isFull ? "calc(100vh - 200px)" : 460 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {columns.map((c) => (
                    <TableCell
                      key={c.key}
                      align={c.align || "left"}
                      sx={{
                        fontWeight: 700,
                        color: "var(--app-text)",
                        backgroundColor: "var(--app-surface-2)",
                        fontFamily: "Figtree",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {c.help ? (
                        <Tooltip title={c.help} arrow placement="top">
                          <Box
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 0.5,
                              cursor: "help",
                            }}
                          >
                            {c.label}
                            <InfoOutlinedIcon
                              sx={{ fontSize: 14, color: "var(--app-text-muted)" }}
                            />
                          </Box>
                        </Tooltip>
                      ) : (
                        c.label
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, i) => (
                  <TableRow key={row.id ?? i} hover sx={getRowSx ? getRowSx(row) : undefined}>
                    {columns.map((c) => (
                      <TableCell
                        key={c.key}
                        align={c.align || "left"}
                        sx={{ fontFamily: "Figtree", color: "var(--app-text)" }}
                      >
                        {c.render ? c.render(row) : row[c.key]}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}

                {totalsRow && (
                  <TableRow>
                    {columns.map((c, idx) => (
                      <TableCell
                        key={c.key}
                        align={c.align || "left"}
                        sx={{
                          position: "sticky",
                          bottom: 0,
                          backgroundColor: "var(--app-surface-2)",
                          fontWeight: 800,
                          color: "var(--app-text)",
                          fontFamily: "Figtree",
                          borderTop: "2px solid var(--app-border)",
                        }}
                      >
                        {idx === 0 && totalsRow[c.key] === undefined
                          ? "Total"
                          : totalsRow[c.key]}
                      </TableCell>
                    ))}
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Collapse>
    </Box>
    </>
  );
};

export default AnalyticsTable;
